import { HttpErrorResponse, HttpEventType, type HttpEvent } from '@angular/common/http';
import { type Signal, signal } from '@angular/core';
import { type Observable, type Subscription, timeout } from 'rxjs';
import {
  ATTEMPT_IDLE_TIMEOUT_MS,
  MAX_ATTEMPTS,
  OFFLINE_PROBE_MS,
  backoffMs,
  classifyTransportError,
} from '../entities/upload-retry-policy';
import type { UploadCircuitBreaker } from './upload-circuit-breaker';

export interface UploadFailure {
  readonly error: unknown;
  readonly status: number;
  readonly kind: 'refused' | 'server' | 'transport';
  readonly refusal: HttpErrorResponse | null;
  readonly timedOut: boolean;
}

export interface UploadQueueHooks {
  /** A slot was taken for `id`; the host marks the row as sending. */
  started(id: string): void;
  progress?(id: string, percent: number, loaded: number, total: number): void;
  /** A non-empty 2xx body: the server accepted the file. */
  answer(id: string, body: unknown): void;
  emptyAnswer?(id: string): void;
  /** The request stream completed (after the answer, or without one). */
  completed(id: string): void;
  retryScheduled?(id: string, wait: { attempts: number; backoffMs: number; offline: boolean }): void;
  /** The backoff elapsed: the host marks the row ready again and the pump re-runs. */
  requeued(id: string): void;
  /** 409: the server already holds this fileId. Absent = a plain refusal. */
  conflict?(id: string, error: HttpErrorResponse): void;
  failed(id: string, failure: UploadFailure): void;
}

/** The host owns the rows; the queue owns slots, attempts, backoff and abort. */
export interface UploadQueueHost {
  readyIds(key: string): readonly string[];
  /** One attempt for `id`, bound to the host's CURRENT row; null = nothing left to send. */
  open(id: string): Observable<HttpEvent<unknown>> | null;
  isEmptyAnswer?(body: unknown): boolean;
  maxAttempts?(id: string): number;
  readonly hooks: UploadQueueHooks;
}

let nextQueueId = 0;

export class UploadQueue {
  private readonly queueId = `q${nextQueueId++}`;
  private readonly inflight = new Map<string, number>();
  private readonly subs = new Map<string, Subscription>();
  private readonly attempts = new Map<string, number>();
  private readonly timers = new Map<string, { readonly timer: ReturnType<typeof setTimeout>; readonly key: string }>();
  private readonly waiting = signal<ReadonlySet<string>>(new Set());
  /** Ids sitting in a retry backoff - the progress bar reads it as «Нет связи». */
  readonly retryWaiting: Signal<ReadonlySet<string>> = this.waiting.asReadonly();

  constructor(
    private readonly host: UploadQueueHost,
    private readonly circuit: UploadCircuitBreaker,
    private readonly slots: number,
  ) {}

  pump(key: string): void {
    // Re-entrant: a request that settles synchronously releases and pumps from inside start(), so every turn
    // re-reads the live slot count and the host's ready list instead of a snapshot taken before it.
    const taken = new Set<string>();
    while ((this.inflight.get(key) ?? 0) < this.slots) {
      if (!this.circuit.admit(this.parkKey(key), () => this.pump(key))) return;
      const id = this.host.readyIds(key).find(ready => !taken.has(ready));
      if (id === undefined) return;
      taken.add(id);
      this.inflight.set(key, (this.inflight.get(key) ?? 0) + 1);
      this.host.hooks.started(id);
      this.start(id, key);
    }
  }

  /** Tears down a live transfer (the request is aborted on unsubscribe) and a pending backoff; frees the slot of a live one. */
  abort(id: string, key: string | null): void {
    const pending = this.timers.get(id);
    if (pending) {
      clearTimeout(pending.timer);
      this.timers.delete(id);
    }

    this.attempts.delete(id);
    this.dropWaiting(id);
    const sub = this.subs.get(id);
    if (!sub) {
      // The backoff timer was the one pump due for this key: a caller that re-queued the job relies on it.
      if (pending && key !== null) this.pump(key);
      return;
    }

    this.subs.delete(id);
    sub.unsubscribe();
    if (key !== null) this.release(key);
  }

  /** The person asked to try now: every waiting file (of `key`, or all) skips the rest of its backoff. */
  skipBackoff(key?: string): void {
    for (const [id, pending] of [...this.timers]) {
      if (key !== undefined && pending.key !== key) continue;
      clearTimeout(pending.timer);
      this.afterBackoff(id, pending.key);
    }
  }

  private start(id: string, key: string): void {
    let attempt$: Observable<HttpEvent<unknown>> | null;
    try {
      attempt$ = this.host.open(id);
    } catch (error) {
      // Nothing left the browser (a missing upload chain, a lane that cannot build the request): no retry helps.
      this.attempts.delete(id);
      try {
        this.host.hooks.failed(id, { error, status: 0, kind: 'transport', refusal: null, timedOut: false });
      } finally {
        this.release(key);
      }

      return;
    }

    if (attempt$ === null) {
      this.release(key);
      return;
    }

    const sub = attempt$.pipe(timeout({ each: ATTEMPT_IDLE_TIMEOUT_MS })).subscribe({
      next: event => this.onEvent(id, event),
      error: (error: unknown) => {
        this.subs.delete(id);
        try {
          this.onError(id, key, error);
        } finally {
          this.release(key);
        }
      },
      complete: () => {
        this.subs.delete(id);
        this.attempts.delete(id);
        try {
          this.host.hooks.completed(id);
        } finally {
          this.release(key);
        }
      },
    });
    // A synchronously failed request has already cleaned up - storing its closed handle would leak.
    if (!sub.closed) this.subs.set(id, sub);
  }

  private onEvent(id: string, event: HttpEvent<unknown>): void {
    if (event.type === HttpEventType.UploadProgress && event.total) {
      this.host.hooks.progress?.(id, Math.round((100 * event.loaded) / event.total), event.loaded, event.total);
      return;
    }

    if (event.type !== HttpEventType.Response) return;
    const body = event.body;
    if (this.host.isEmptyAnswer?.(body)) {
      this.host.hooks.emptyAnswer?.(id);
      return;
    }

    if (body === null || body === undefined) return;
    this.circuit.recordAccepted();
    this.host.hooks.answer(id, body);
  }

  private onError(id: string, key: string, error: unknown): void {
    const info = classifyTransportError(error);
    // 409 = the server holds this fileId or its upload lock; re-posting can never win, so the host reconciles instead.
    if (info.status === 409 && this.host.hooks.conflict) {
      this.circuit.recordAccepted();
      this.attempts.delete(id);
      this.host.hooks.conflict(id, error as HttpErrorResponse);
      return;
    }

    // The browser knows the link is down: park the job without burning attempts, the probe loop resumes it.
    const offline = typeof navigator !== 'undefined' && !navigator.onLine;
    // Only online transient failures feed the breaker: offline has its own probe, and a 4xx says nothing about the link.
    if (info.transient && !offline) this.circuit.recordFailure(this.parkKey(key), () => this.pump(key));
    const limit = this.host.maxAttempts?.(id) ?? MAX_ATTEMPTS;
    const used = this.attempts.get(id) ?? 0;
    if (info.transient && limit > 1 && (offline || used + 1 < limit)) {
      const next = offline ? used : used + 1;
      this.attempts.set(id, next);
      const wait = offline ? OFFLINE_PROBE_MS : backoffMs(used);
      this.waiting.update(set => new Set(set).add(id));
      this.host.hooks.retryScheduled?.(id, { attempts: next, backoffMs: wait, offline });
      this.timers.set(id, { key, timer: setTimeout(() => this.afterBackoff(id, key), wait) });
      return;
    }

    this.attempts.delete(id);
    this.host.hooks.failed(id, {
      error,
      status: info.status,
      kind: info.kind,
      refusal: info.refusal,
      timedOut: info.timedOut,
    });
  }

  private afterBackoff(id: string, key: string): void {
    this.timers.delete(id);
    this.dropWaiting(id);
    this.host.hooks.requeued(id);
    this.pump(key);
  }

  private dropWaiting(id: string): void {
    this.waiting.update(set => {
      if (!set.has(id)) return set;
      const next = new Set(set);
      next.delete(id);
      return next;
    });
  }

  private release(key: string): void {
    this.inflight.set(key, Math.max(0, (this.inflight.get(key) ?? 1) - 1));
    this.pump(key);
  }

  private parkKey(key: string): string {
    return `${this.queueId}|${key}`;
  }
}
